import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
	Box,
	Typography,
	Card,
	CardContent,
	CardActions,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Button,
	CircularProgress,
	Chip,
	Container,
	Paper,
	Divider,
	TextField,
	Grid,
	Avatar,
	FormControlLabel,
	Switch
} from '@mui/material'
import { 
	Science as ScienceIcon, 
	CheckCircle, 
	Cancel, 
	Warning,
	Visibility as VisibilityIcon
} from '@mui/icons-material'
import { useBatch } from '../hooks/useBatch'
import { useCrop } from '../hooks/useCrop'
import { useCategory } from '../hooks/useCategory'
import { TestHistoryFilters } from '../models/batch'
import { Germplasm } from '../models/crop'

interface TestRecord {
	_id: string;
	uid: string; // MongoDB ObjectId for navigation
	batchId: string;
	crop: string;
	germplasm: string;
	category: string;
	owner: string;
	testNumber: number; // Test number within the batch
	totalTests: number; // Total tests in the batch
	test: {
		user: string;
		beginDate: string;
		endDate: string;
		result: number;
		attachments: string[];
	};
}

const TestHistory: React.FC = () => {
	const navigate = useNavigate()
	const { generateTestHistory, loadingReport } = useBatch()
	const { crops, fetchCrops } = useCrop()
	const { categories, fetchCategories } = useCategory()

	const [tests, setTests] = useState<TestRecord[]>([])
	const [total, setTotal] = useState<number>(0)
	const [filters, setFilters] = useState<TestHistoryFilters>({})
	const [selectedCrop, setSelectedCrop] = useState<string>('')
	const [germplasms, setGermplasms] = useState<Germplasm[]>([])
	const [includeInactive, setIncludeInactive] = useState<boolean>(false)

	useEffect(() => {
		fetchCrops()
		fetchCategories()
	}, [])

	useEffect(() => {
		if (selectedCrop) {
			const crop = crops.find(c => c.name === selectedCrop)
			if (crop) {
				setGermplasms(crop.germplasm || [])
			}
		} else {
			setGermplasms([])
		}
	}, [selectedCrop, crops])

	const handleSearch = async () => {
		const searchFilters = {
			...filters,
			...(includeInactive && { includeInactive: 'true' })
		}
		const result = await generateTestHistory(searchFilters)
		if (result) {
			setTests(result.tests)
			setTotal(result.total)
		}
	}

	const handleFilterChange = (field: keyof TestHistoryFilters, value: string | undefined) => {
		setFilters(prev => ({
			...prev,
			[field]: value || undefined
		}))
	}

	const handleCropChange = (cropName: string) => {
		setSelectedCrop(cropName)
		handleFilterChange('crop', cropName || undefined)
		handleFilterChange('germplasm', undefined) // Reset germplasm when crop changes
	}

	const handleResetFilters = () => {
		setFilters({})
		setSelectedCrop('')
		setIncludeInactive(false)
		setTests([])
		setTotal(0)
	}

	const handleManageBatch = (uid: string) => {
		navigate('/home/administrar-lote', { state: { batchId: uid } })
	}

	const hasActiveFilters = Object.values(filters).some(value => value !== undefined) || includeInactive

	const getResultIcon = (result: number) => {
		if (result >= 85) {
			return <CheckCircle sx={{ color: '#4caf50' }} />
		} else if (result >= 70) {
			return <Warning sx={{ color: '#ff9800' }} />
		} else {
			return <Cancel sx={{ color: '#f44336' }} />
		}
	}

	const getResultLabel = (result: number) => {
		if (result >= 85) {
			return 'Excelente'
		} else if (result >= 70) {
			return 'Aceptable'
		} else {
			return 'Deficiente'
		}
	}

	const getResultColor = (result: number) => {
		if (result >= 85) {
			return '#4caf50'
		} else if (result >= 70) {
			return '#ff9800'
		} else {
			return '#f44336'
		}
	}

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('es-ES', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		})
	}

	const generateTestDisplayId = (testRecord: TestRecord) => {
		return `Test-${testRecord.testNumber}-${testRecord.batchId}`
	}

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			<Paper elevation={3} sx={{ p: 3, mb: 3 }}>
				<Box display="flex" alignItems="center" gap={2} mb={3}>
					<ScienceIcon sx={{ fontSize: 32, color: '#177C3B' }} />
					<Typography variant="h4" component="h1" >
						Historial de Pruebas de Germinación
					</Typography>
				</Box>

				{/* Filters */}
				<Box sx={{ mb: 3 }}>
					<Typography variant="h6" sx={{ mb: 2 }}>
						Filtros de Búsqueda
					</Typography>
					<Grid container spacing={2} alignItems="end">
						<Grid item xs={12} sm={6} md={2}>
							<FormControl fullWidth size="small">
								<InputLabel>Cultivo</InputLabel>
								<Select
									value={selectedCrop}
									label="Cultivo"
									onChange={(e) => handleCropChange(e.target.value)}
								>
									<MenuItem value="">Todos</MenuItem>
									{crops.map((crop) => (
										<MenuItem key={crop.uid} value={crop.name}>
											{crop.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={2}>
							<FormControl fullWidth size="small">
								<InputLabel>Germoplasma</InputLabel>
								<Select
									value={filters.germplasm || ''}
									label="Germoplasma"
									onChange={(e) => handleFilterChange('germplasm', e.target.value)}
									disabled={!selectedCrop}
								>
									<MenuItem value="">Todos</MenuItem>
									{germplasms.map((germplasm) => (
										<MenuItem key={germplasm._id} value={germplasm.name}>
											{germplasm.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={2}>
							<FormControl fullWidth size="small">
								<InputLabel>Categoría</InputLabel>
								<Select
									value={filters.category || ''}
									label="Categoría"
									onChange={(e) => handleFilterChange('category', e.target.value)}
								>
									<MenuItem value="">Todas</MenuItem>
									{categories.map((category) => (
										<MenuItem key={category.uid} value={category.name}>
											{category.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={2}>
							<TextField
								fullWidth
								size="small"
								label="ID del Lote"
								value={filters.batchId || ''}
								onChange={(e) => handleFilterChange('batchId', e.target.value)}
								placeholder="Buscar por ID..."
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={2}>
							<TextField
								fullWidth
								size="small"
								type="date"
								label="Fecha Inicio"
								value={filters.startDate || ''}
								onChange={(e) => handleFilterChange('startDate', e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={2}>
							<TextField
								fullWidth
								size="small"
								type="date"
								label="Fecha Fin"
								value={filters.endDate || ''}
								onChange={(e) => handleFilterChange('endDate', e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={2}>
							<Button
								fullWidth
								variant="contained"
								onClick={handleSearch}
								disabled={loadingReport}
								sx={{ py: 1.5 }}
							>
								{loadingReport ? <CircularProgress size={20} /> : 'Buscar'}
							</Button>
						</Grid>

						<Grid item xs={12} sm={6} md={2}>
							<Button
								fullWidth
								variant="outlined"
								onClick={handleResetFilters}
								disabled={loadingReport || !hasActiveFilters}
								sx={{ py: 1.5 }}
							>
								Limpiar
							</Button>
						</Grid>
					</Grid>

					<Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
						<FormControlLabel
							control={
								<Switch
									checked={includeInactive}
									onChange={(e) => setIncludeInactive(e.target.checked)}
									color="primary"
								/>
							}
							label="Incluir lotes desactivados"
						/>
					</Box>
				</Box>

				{/* Active Filters */}
				{hasActiveFilters && (
					<Box sx={{ mb: 3 }}>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							Filtros Activos:
						</Typography>
						<Box display="flex" gap={1} flexWrap="wrap">
							{filters.crop && (
								<Chip
									label={`Cultivo: ${filters.crop}`}
									size="small"
									onDelete={() => {
										handleFilterChange('crop', undefined)
										setSelectedCrop('')
									}}
								/>
							)}
							{filters.germplasm && (
								<Chip
									label={`Germoplasma: ${filters.germplasm}`}
									size="small"
									onDelete={() => handleFilterChange('germplasm', undefined)}
								/>
							)}
							{filters.category && (
								<Chip
									label={`Categoría: ${filters.category}`}
									size="small"
									onDelete={() => handleFilterChange('category', undefined)}
								/>
							)}
							{filters.batchId && (
								<Chip
									label={`ID: ${filters.batchId}`}
									size="small"
									onDelete={() => handleFilterChange('batchId', undefined)}
								/>
							)}
							{filters.startDate && (
								<Chip
									label={`Desde: ${formatDate(filters.startDate)}`}
									size="small"
									onDelete={() => handleFilterChange('startDate', undefined)}
								/>
							)}
							{filters.endDate && (
								<Chip
									label={`Hasta: ${formatDate(filters.endDate)}`}
									size="small"
									onDelete={() => handleFilterChange('endDate', undefined)}
								/>
							)}
						</Box>
					</Box>
				)}
			</Paper>

			{/* Results Summary */}
			{tests.length > 0 && (
				<Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
					<Typography variant="h6" color="primary">
						Resumen de Resultados
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Se encontraron <strong>{total}</strong> pruebas de germinación
					</Typography>
				</Paper>
			)}

			{/* Test Cards */}
			{tests.length > 0 ? (
				<Grid container spacing={2}>
					{tests.map((testRecord) => (
						<Grid item xs={12} sm={6} md={4} lg={3} key={testRecord._id}>
							<Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
								<CardContent sx={{ flexGrow: 1 }}>
									<Box display="flex" alignItems="center" gap={1} mb={2}>
										<Avatar sx={{ bgcolor: getResultColor(testRecord.test.result), width: 32, height: 32 }}>
											{getResultIcon(testRecord.test.result)}
										</Avatar>
										<Box>
											<Typography variant="h6" component="div" noWrap>
												{generateTestDisplayId(testRecord)}
											</Typography>
											<Chip
												label={getResultLabel(testRecord.test.result)}
												size="small"
												sx={{ bgcolor: getResultColor(testRecord.test.result), color: 'white' }}
											/>
										</Box>
									</Box>

									<Divider sx={{ my: 1 }} />

									<Box sx={{ mb: 1 }}>
										<Typography variant="body2" color="text.secondary">
											<strong>Lote:</strong> {testRecord.batchId}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											<strong>Test:</strong> {testRecord.testNumber} de {testRecord.totalTests}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											<strong>Resultado:</strong> {testRecord.test.result}%
										</Typography>
										<Typography variant="body2" color="text.secondary">
											<strong>Fecha Inicio:</strong> {formatDate(testRecord.test.beginDate)}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											<strong>Fecha Fin:</strong> {formatDate(testRecord.test.endDate)}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											<strong>Técnico:</strong> {testRecord.test.user}
										</Typography>
									</Box>

									<Divider sx={{ my: 1 }} />

									<Box>
										<Typography variant="caption" color="text.secondary">
											<strong>Cultivo:</strong> {testRecord.crop}
										</Typography>
										<br />
										<Typography variant="caption" color="text.secondary">
											<strong>Germoplasma:</strong> {testRecord.germplasm}
										</Typography>
										<br />
										<Typography variant="caption" color="text.secondary">
											<strong>Categoría:</strong> {testRecord.category}
										</Typography>
									</Box>
								</CardContent>

								<CardActions>
									<Button
										size="small"
										variant="contained"
										startIcon={<VisibilityIcon />}
										onClick={() => handleManageBatch(testRecord.uid)}
										fullWidth
									>
										Ver Lote
									</Button>
								</CardActions>
							</Card>
						</Grid>
					))}
				</Grid>
			) : (
				!loadingReport && (
					<Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
						<ScienceIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
						<Typography variant="h6" color="text.secondary">
							No se encontraron pruebas de germinación
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Utiliza los filtros para buscar pruebas específicas
						</Typography>
					</Paper>
				)
			)}

			{loadingReport && (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			)}
		</Container>
	)
}

export default TestHistory
